import { makeAutoObservable, runInAction } from "mobx";
import { IsNotEmpty, IsString, validate } from "class-validator";
import {
  adminFeedbackList,
  adminFeedbackGetOne,
  adminFeedbackUpdateStatus,
  adminFeedbackAddComment,
  adminFeedbackTopicList,
} from "@pro-sto/api-client";
import type {
  FeedbackResponseDto,
  FeedbackDetailResponseDto,
  FeedbackTimelineEventDto,
  PaginationMetaDto,
  TopicResponseDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";

export type FeedbackStatus = FeedbackDetailResponseDto["status"];

class FeedbackCommentDto {
  @IsString()
  @IsNotEmpty({ message: "Введите комментарий" })
  text = "";
}

function sortTimeline(
  timeline: FeedbackTimelineEventDto[],
): FeedbackTimelineEventDto[] {
  return [...timeline].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
}

function parseListResponse(
  raw: unknown,
  fallbackLimit: number,
): { items: FeedbackResponseDto[]; meta: PaginationMetaDto } {
  const r = raw as Record<string, unknown>;
  const items =
    (r["items"] as FeedbackResponseDto[] | undefined) ??
    (r["data"] as FeedbackResponseDto[] | undefined) ??
    (Array.isArray(raw) ? (raw as FeedbackResponseDto[]) : undefined) ??
    [];
  const nestedMeta = r["meta"] as Partial<PaginationMetaDto> | undefined;
  const total = nestedMeta?.total ?? (r["total"] as number | undefined) ?? 0;
  const page = nestedMeta?.page ?? (r["page"] as number | undefined) ?? 1;
  const limit =
    nestedMeta?.limit ?? (r["limit"] as number | undefined) ?? fallbackLimit;
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
  const meta: PaginationMetaDto = {
    total,
    page,
    limit,
    totalPages,
    hasNextPage: nestedMeta?.hasNextPage ?? page < totalPages,
    hasPrevPage: nestedMeta?.hasPrevPage ?? page > 1,
  };
  return { items, meta };
}

class FeedbackStore {
  items: FeedbackResponseDto[] = [];
  meta: PaginationMetaDto | null = null;
  isLoading = false;
  isLoadingMore = false;

  statusFilter: FeedbackStatus | null = null;
  topicFilter: string | null = null;
  search = "";
  isUserTypingSearch = false;

  topics: TopicResponseDto[] = [];
  isTopicsLoading = false;

  isDrawerOpen = false;
  selectedUuid: string | null = null;
  selectedFeedback: FeedbackDetailResponseDto | null = null;
  isDetailLoading = false;
  isStatusUpdating = false;
  private detailLoadedUuid: string | null = null;

  commentText = "";
  commentSubmitAttempted = false;
  commentErrors: Record<string, string> = {};
  isCommentSubmitting = false;

  page = 1;
  readonly limit = 20;

  constructor() {
    makeAutoObservable(this);
  }

  get hasNextPage(): boolean {
    return this.meta?.hasNextPage ?? false;
  }

  get hasActiveFilters(): boolean {
    return this.statusFilter !== null || this.topicFilter !== null;
  }

  get isUiBusy(): boolean {
    return this.isLoading && !this.isUserTypingSearch;
  }

  private listParams(page: number) {
    return {
      page,
      limit: this.limit,
      status: this.statusFilter ?? undefined,
      topicUuid: this.topicFilter ?? undefined,
      search: this.search || undefined,
    };
  }

  loadAll = async () => {
    this.isLoading = true;
    try {
      const [listResponse] = await Promise.all([
        adminFeedbackList(this.listParams(1)),
        this.loadTopics(),
      ]);
      const { items, meta } = parseListResponse(listResponse, this.limit);
      runInAction(() => {
        this.items = items;
        this.meta = meta;
        this.page = 1;
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить обращения");
    }
  };

  loadMore = async () => {
    if (this.isLoadingMore || !this.hasNextPage) return;
    this.isLoadingMore = true;
    const nextPage = this.page + 1;
    try {
      const response = await adminFeedbackList(this.listParams(nextPage));
      const { items: newItems, meta } = parseListResponse(response, this.limit);
      runInAction(() => {
        const existing = new Set(this.items.map((item) => item.uuid));
        const unique = newItems.filter((item) => !existing.has(item.uuid));
        this.items = [...this.items, ...unique];
        this.meta = meta;
        this.page = nextPage;
        this.isLoadingMore = false;
      });
    } catch {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  };

  loadTopics = async (force = false) => {
    if (!force && this.topics.length > 0) return;
    this.isTopicsLoading = true;
    try {
      const response = await adminFeedbackTopicList();
      runInAction(() => {
        this.topics = response ?? [];
        this.isTopicsLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isTopicsLoading = false;
      });
    }
  };

  invalidateTopicsCache = () => {
    this.topics = [];
  };

  setStatusFilter = (status: FeedbackStatus | null) => {
    this.statusFilter = status;
    this.page = 1;
    this.loadAll();
  };

  setTopicFilter = (topicUuid: string | null) => {
    this.topicFilter = topicUuid;
    this.page = 1;
    this.loadAll();
  };

  setSearch = (value: string) => {
    this.search = value;
    this.page = 1;
    this.loadAll();
  };

  setIsUserTypingSearch = (value: boolean) => {
    this.isUserTypingSearch = value;
  };

  resetFilters = () => {
    this.statusFilter = null;
    this.topicFilter = null;
    this.page = 1;
    this.loadAll();
  };

  openDrawer = async (
    uuid: string,
    options?: { startWork?: boolean },
  ): Promise<boolean> => {
    const shouldFetch =
      this.detailLoadedUuid !== uuid ||
      this.selectedFeedback?.uuid !== uuid;

    this.selectedUuid = uuid;
    this.isDrawerOpen = true;

    if (shouldFetch) {
      this.isDetailLoading = true;
      this.selectedFeedback = null;

      try {
        const response = await adminFeedbackGetOne({ uuid });
        runInAction(() => {
          this.selectedFeedback = response;
          this.detailLoadedUuid = uuid;
          this.isDetailLoading = false;
        });
      } catch {
        runInAction(() => {
          this.isDetailLoading = false;
          this.resetDrawer();
        });
        toastStore.showError("Не удалось загрузить обращение");
        return false;
      }
    }

    if (options?.startWork) {
      await this.applyStartWork();
    }

    return true;
  };

  applyStartWork = async (): Promise<boolean> => {
    if (!this.selectedFeedback || !this.selectedUuid) return false;
    if (this.selectedFeedback.status !== "new") return true;

    return this.updateStatus("in_progress", {
      successMessage: "Обращение взято в работу",
    });
  };

  resetDrawer = () => {
    this.isDrawerOpen = false;
    this.selectedUuid = null;
    this.selectedFeedback = null;
    this.isDetailLoading = false;
    this.detailLoadedUuid = null;
    this.commentText = "";
    this.commentSubmitAttempted = false;
    this.commentErrors = {};
    this.isCommentSubmitting = false;
  };

  closeDrawer = () => {
    this.resetDrawer();
  };

  setCommentText = (value: string) => {
    this.commentText = value;
  };

  submitComment = async (): Promise<boolean> => {
    if (!this.selectedUuid) return false;

    this.commentSubmitAttempted = true;
    const dto = Object.assign(new FeedbackCommentDto(), {
      text: this.commentText.trim(),
    });
    const errors = await validate(dto);
    if (errors.length > 0) {
      runInAction(() => {
        this.commentErrors = {
          text: Object.values(errors[0]?.constraints ?? {})[0] ?? "",
        };
      });
      return false;
    }

    this.isCommentSubmitting = true;

    try {
      const event = await adminFeedbackAddComment({
        uuid: this.selectedUuid,
        requestBody: { text: dto.text },
      });
      runInAction(() => {
        if (this.selectedFeedback) {
          this.selectedFeedback = {
            ...this.selectedFeedback,
            timeline: sortTimeline([
              ...this.selectedFeedback.timeline,
              event,
            ]),
          };
        }
        this.commentText = "";
        this.commentSubmitAttempted = false;
        this.commentErrors = {};
        this.isCommentSubmitting = false;
      });
      toastStore.showSuccess("Комментарий добавлен");
      return true;
    } catch {
      runInAction(() => {
        this.isCommentSubmitting = false;
      });
      toastStore.showError("Не удалось отправить комментарий");
      return false;
    }
  };

  updateStatus = async (
    status: FeedbackStatus,
    options?: { successMessage?: string; showErrorToast?: boolean },
  ): Promise<boolean> => {
    if (!this.selectedUuid) return false;
    this.isStatusUpdating = true;
    try {
      const response = await adminFeedbackUpdateStatus({
        uuid: this.selectedUuid,
        requestBody: { status },
      });
      runInAction(() => {
        this.selectedFeedback = response;
        this.detailLoadedUuid = response.uuid;
        this.items = this.items.map((item) =>
          item.uuid === response.uuid
            ? {
                ...item,
                status: response.status,
                updated_at: response.updated_at,
              }
            : item,
        );
        this.isStatusUpdating = false;
      });
      toastStore.showSuccess(
        options?.successMessage ?? "Статус обновлён",
      );
      return true;
    } catch {
      runInAction(() => {
        this.isStatusUpdating = false;
      });
      if (options?.showErrorToast !== false) {
        toastStore.showError("Не удалось обновить статус");
      }
      return false;
    }
  };
}

export const feedbackStore = new FeedbackStore();
export type { FeedbackStore };
